import { Suspense, lazy } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { DateFilterProvider } from "@/contexts/DateFilterContext";
import { BonusAuditProvider } from "@/contexts/BonusAuditContext";
import { ScraperProvider } from "@/contexts/ScraperContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { Loader2 } from "lucide-react";

// Eagerly loaded (landing page – avoids critical request chain)
import Login from "./pages/Login";

// Lazy-loaded pages
const Dashboard = lazy(() => import("./pages/Dashboard"));
const WorkScreen = lazy(() => import("./pages/WorkScreen"));
const FinanceBoard = lazy(() => import("./pages/FinanceBoard"));
const FinanceAnalyticsPage = lazy(() => import("./pages/FinanceAnalytics"));
const Lists = lazy(() => import("./pages/Lists"));
const ListDetail = lazy(() => import("./pages/ListDetail"));
const ImportLists = lazy(() => import("./pages/ImportLists"));
const Deals = lazy(() => import("./pages/Deals"));

const DealDetails = lazy(() => import("./pages/DealDetails"));
const Services = lazy(() => import("./pages/Services"));
const ServiceCatalog = lazy(() => import("./pages/ServiceCatalog"));
const Reports = lazy(() => import("./pages/Reports"));
const ReportsDefault = lazy(() => import("./pages/reports/ReportsDefault"));
const DynamicReport = lazy(() => import("./pages/reports/DynamicReport"));
const DynamicChart = lazy(() => import("./pages/reports/DynamicChart"));
const AgentBonusReport = lazy(() => import("./pages/reports/AgentBonusReport"));
const StatusHistory = lazy(() => import("./pages/reports/StatusHistory"));
const PersonalsReport = lazy(() => import("./pages/reports/PersonalsReport"));
const TrashCancelList = lazy(() => import("./pages/reports/TrashCancelList"));
const CantCallList = lazy(() => import("./pages/reports/CantCallList"));
const PaidList = lazy(() => import("./pages/reports/PaidList"));
const Settings = lazy(() => import("./pages/Settings"));
const ClientProfile = lazy(() => import("./pages/ClientProfile"));

const TeamAndAgents = lazy(() => import("./pages/TeamAndAgents"));
const TeamsPage = lazy(() => import("./pages/teamAgents/TeamsPage"));
const AgentsPage = lazy(() => import("./pages/teamAgents/AgentsPage"));
const TeamReportPage = lazy(() => import("./pages/teamAgents/TeamReportPage"));
const AgentReportPage = lazy(() => import("./pages/teamAgents/AgentReportPage"));
const AgentActivityPage = lazy(() => import("./pages/teamAgents/AgentActivityPage"));
const WorkHoursPage = lazy(() => import("./pages/teamAgents/WorkHoursPage"));
const CountriesPage = lazy(() => import("./pages/teamAgents/CountriesPage"));
const AgentProfile = lazy(() => import("./pages/AgentProfile"));
const TeamBonus = lazy(() => import("./pages/TeamBonus"));
const DailyTargets = lazy(() => import("./pages/DailyTargets"));
const BonusHistory = lazy(() => import("./pages/BonusHistory"));
const BonusRules = lazy(() => import("./pages/BonusRules"));
const NotFound = lazy(() => import("./pages/NotFound"));
const FinanceOrders = lazy(() => import("./pages/FinanceOrders"));
const Investments = lazy(() => import("./pages/finance/Investments"));
const ProformaInvoices = lazy(() => import("./pages/finance/ProformaInvoices"));
const PaymentFlow = lazy(() => import("./pages/finance/PaymentFlow"));
const CountryCostCenter = lazy(() => import("./pages/finance/CountryCostCenter"));
const ImportAdvanced = lazy(() => import("./pages/ImportAdvanced"));
const DuplicateManager = lazy(() => import("./pages/DuplicateManager"));
const Assigner = lazy(() => import("./pages/Assigner"));
const ListStatistics = lazy(() => import("./pages/ListStatistics"));
const ListStatisticsAdvanced = lazy(() => import("./pages/ListStatisticsAdvanced"));
const LeadScraper = lazy(() => import("./pages/LeadScraper"));
const NotificationsInbox = lazy(() => import("./pages/notifications/NotificationsInbox"));
const SendCustomNotification = lazy(() => import("./pages/notifications/SendCustomNotification"));
const KitchenOrder = lazy(() => import("./pages/kitchen/KitchenOrder"));
const KitchenManager = lazy(() => import("./pages/kitchen/KitchenManager"));
const BalanceManagement = lazy(() => import("./pages/kitchen/BalanceManagement"));
const KitchenOrdersPage = lazy(() => import("./pages/kitchen/KitchenOrdersPage"));
const MyOrders = lazy(() => import("./pages/kitchen/MyOrders"));
const TransactionHistory = lazy(() => import("./pages/kitchen/TransactionHistory"));
const OrderHistory = lazy(() => import("./pages/kitchen/OrderHistory"));
const ItemsReport = lazy(() => import("./pages/kitchen/ItemsReport"));

const Dialer = lazy(() => import("./pages/Dialer"));


const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const PageLoader = () => (
  <div className="flex items-center justify-center h-full min-h-[200px]">
    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <BonusAuditProvider>
          <DateFilterProvider>
            <ScraperProvider>
              <TooltipProvider>
                <Toaster />
                <Sonner />
                <BrowserRouter>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                    <Route element={<MainLayout />}>
                      <Route path="/dashboard" element={<Dashboard />} />
                      <Route path="/work" element={<WorkScreen />} />
                      <Route path="/work/finance-board" element={<FinanceBoard />} />
                      <Route path="/work/finance-analytics" element={<FinanceAnalyticsPage />} />
                      <Route path="/lists" element={<Lists />} />
                      <Route path="/lists/import" element={<ImportLists />} />
                      <Route path="/lists/import-advanced" element={<ImportAdvanced />} />
                      <Route path="/lists/statistics" element={<ListStatistics />} />
                      <Route path="/lists/statistics-advanced" element={<ListStatisticsAdvanced />} />
                      <Route path="/lists/assigner" element={<Assigner />} />
                      <Route path="/lists/scraper" element={<LeadScraper />} />
                      <Route path="/dialer" element={<Dialer />} />
                      <Route path="/lists/:listId" element={<ListDetail />} />
                      <Route path="/import" element={<ImportLists />} />
                      <Route path="/sales" element={<Deals />} />
                      <Route path="/deals" element={<Deals />} />
                      <Route path="/deals/:dealId" element={<DealDetails />} />
                      <Route path="/my-clients" element={<Navigate to="/deals" replace />} />
                      <Route path="/services" element={<Services />} />
                      <Route path="/services/catalog" element={<ServiceCatalog />} />
                      <Route path="/compensation" element={<Navigate to="/team-agents" replace />} />
                      <Route path="/my-compensation" element={<Navigate to="/team-agents/agent-report" replace />} />
                      <Route path="/analytics/owner" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/analytics/profit" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/analytics/leaderboard" element={<Navigate to="/team-agents" replace />} />
                      <Route path="/analytics/utilization" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/analytics/cancellations" element={<Navigate to="/reports" replace />} />
                      <Route path="/analytics/bottlenecks" element={<Navigate to="/reports" replace />} />
                      <Route path="/insights" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/my-insights" element={<Navigate to="/dashboard" replace />} />
                      <Route path="/team-agents" element={<TeamAndAgents />} />
                      <Route path="/team-agents/teams" element={<TeamsPage />} />
                      <Route path="/team-agents/agents" element={<AgentsPage />} />
                      <Route path="/team-agents/team-report" element={<TeamReportPage />} />
                      <Route path="/team-agents/agent-report" element={<AgentReportPage />} />
                      <Route path="/team-agents/activity" element={<AgentActivityPage />} />
                      <Route path="/team-agents/work-hours" element={<WorkHoursPage />} />
                      <Route path="/team-agents/countries" element={<CountriesPage />} />
                      <Route path="/agent/:id" element={<AgentProfile />} />
                      <Route path="/team-bonus" element={<DailyTargets />} />
                      <Route path="/team-bonus/rules" element={<BonusRules />} />
                      <Route path="/team-bonus/history" element={<BonusHistory />} />
                      <Route path="/users" element={<Navigate to="/team-agents/agents" replace />} />
                      <Route path="/reports" element={<ReportsDefault />} />
                      <Route path="/reports/dynamic" element={<DynamicReport />} />
                      <Route path="/reports/chart" element={<DynamicChart />} />
                      <Route path="/reports/agent-bonus" element={<AgentBonusReport />} />
                      <Route path="/reports/status-history" element={<StatusHistory />} />
                      <Route path="/reports/personals" element={<PersonalsReport />} />
                      <Route path="/reports/trash-cancel" element={<TrashCancelList />} />
                      <Route path="/reports/cant-call" element={<CantCallList />} />
                      <Route path="/reports/paid" element={<PaidList />} />
                      <Route path="/finance" element={<Navigate to="/finance/proforma" replace />} />
                      <Route path="/finance/orders" element={<FinanceOrders />} />
                      <Route path="/finance/investments" element={<Investments />} />
                      <Route path="/finance/vat" element={<Navigate to="/finance/proforma" replace />} />
                      <Route path="/finance/proforma" element={<ProformaInvoices />} />
                      <Route path="/finance/payment-flow" element={<PaymentFlow />} />
                      <Route path="/finance/country-costs" element={<CountryCostCenter />} />
                      <Route path="/payroll" element={<Navigate to="/team-agents" replace />} />
                      <Route path="/payroll/bonus" element={<Navigate to="/team-bonus/rules" replace />} />
                      <Route path="/payroll/expenses" element={<Navigate to="/team-agents" replace />} />
                      <Route path="/payroll/summary" element={<Navigate to="/team-agents/team-report" replace />} />
                      <Route path="/payroll/tax-config" element={<Navigate to="/settings" replace />} />
                      <Route path="/payroll/attendance" element={<Navigate to="/team-agents/work-hours" replace />} />
                      <Route path="/kitchen" element={<KitchenOrder />} />
                      <Route path="/kitchen/manager" element={<KitchenManager />} />
                      <Route path="/kitchen/balance" element={<BalanceManagement />} />
                      <Route path="/kitchen/orders" element={<KitchenOrdersPage />} />
                      <Route path="/kitchen/my-orders" element={<MyOrders />} />
                      <Route path="/kitchen/transactions" element={<TransactionHistory />} />
                      <Route path="/kitchen/history" element={<OrderHistory />} />
                      <Route path="/kitchen/report" element={<ItemsReport />} />
                      <Route path="/notifications" element={<NotificationsInbox />} />
                      <Route path="/notifications/send" element={<SendCustomNotification />} />
                      <Route path="/duplicate-manager" element={<DuplicateManager />} />
                      <Route path="/settings" element={<Settings />} />
                      <Route path="/settings/permissions" element={<Settings />} />
                      <Route path="/settings/countries" element={<Settings />} />
                      <Route path="/settings/currencies" element={<Settings />} />
                      <Route path="/client/:id" element={<ClientProfile />} />
                      <Route path="/clients/:id" element={<ClientProfile />} />
                    </Route>
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
            </ScraperProvider>
          </DateFilterProvider>
        </BonusAuditProvider>
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
